<template>
    <div v-bind="$attrs">
        <BaseButton 
            id="reportTypeFilter" 
            class="btn--filter dropdown-toggle" 
            type="BaseButton" 
            variant="outline" 
            variant-style="secondary"
            aria-expanded="false" 
            data-bs-toggle="dropdown" 
            data-bs-auto-close="outside"
            is-content
        >            
            <template #content>
                <div class="w-100 justify-content-between flex align-items-center gap-1">
                    <span class="text--xs">Date range</span>
                    <span v-if="filter.range" class="text--xs text-gray-300">|</span>
                    <span class="text--xs text--color-primary">{{ rangeMap[filter?.range] }}</span>
                </div>
            </template>
        </BaseButton>

        <Transition name="date-filter-dropdown">
            <div class="dropdown-menu">
                <form @submit.prevent="">
                    <div class="p-2">
                        <div class="flex gap-4">
                            <div class="flex-1 filter-dates-selection gap-2 grid">
                                <div class="mt--10">
                                    <FormLabel title="Filter By" class="mb--5" label-type="menu" info-type="required" />
    
                                    <div v-for="item in filterByItems" :key="item" class="form-check text--capitalize">
                                        <input
                                            :id="`${item.id}`" 
                                            v-model="filter.filterBy" 
                                            class="form-check-input" 
                                            type="radio"
                                            name="inlineRadioOptions" 
                                            :value="item.id"
                                        >
                                        <label class="form-check-label text--sm" for="status">{{ item.name }}</label>
                                    </div>
                                </div>

                                <div class="mb--15">
                                    <FormLabel title="Date Range" class="mb--5" label-type="menu" info-type="required" />
                                    
                                    <div class="grid gap-1">
                                        <div   
                                            v-for="item in rangePeriod" 
                                            :key="item.id" 
                                            class="date-filter-item" 
                                            :class="{ 'active': item.id === filter.range }"
                                            @click="setCurrentFilter(item)"
                                        >
                                            <p class="text--sm">
                                                {{ item.name }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="flex-2 mt--10">
                                <div class="filter-dates mb--20">
                                    <div class="form-control form-control-sm disabled" style="background-color: #ced4da45;">
                                        {{ $format_date_time(filter.startDate) }}
                                    </div>

                                    <div class="form-control form-control-sm disabled" style="background-color: #ced4da45;">
                                        {{ $format_date_time(filter.endDate) }}
                                    </div>
                                </div>

                                <div class="filter-dates">
                                    <div>
                                        <v-calendar 
                                            v-model="startDate" 
                                            transparent 
                                            borderless
                                            class="text--sm" 
                                            :attributes="calendarAttributes"
                                            :max-date="minmax.minDate"
                                        />
                                    </div>

                                    <div>
                                        <v-calendar 
                                            v-model="endDate" 
                                            transparent 
                                            borderless 
                                            class="text--sm" 
                                            :attributes="calendarAttributes"
                                            :min-date="minmax.maxDate"
                                        />
                                    </div>
                                </div>

                                <div class="flexjustify-content-end w-100 mt--20 gap-2">
                                    <BaseButton  
                                        :disabled="disabled" 
                                        label="Clear all" 
                                        variant="outline"
                                        type="submit" 
                                        @submit="clearFilter" 
                                    />
        
                                    <BaseButton  
                                        :disabled="disabled" 
                                        label="Run filter" 
                                        type="submit" 
                                        @submit="handleFilter" 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Transition>
    </div>
</template>

<script>
import { formatDate } from "@/plugins/filters";
import BaseButton from "../buttons/BaseButton.vue";
import FormLabel from "../forms/FormLabel.vue";
import { serilaizeQuery } from "@/utils/helpers";
import { extractDateFilters } from "@/utils/date";

export default {
    name: "DateRangeFilter",

    components: {
        FormLabel,
        BaseButton,
        FormLabel,
    },

    props: {
        disabled: {
            type: Boolean,
            default: false,
        },

        pageType: {
            type: String,
            default: "page", // inner-page, page
        },

        page: {
            type: String,
            default: "", // invoices, projects... used to determine the filter by items to show
        },
    },

    emits: ["update:filter"],
    
    data() {
        return {
            filter: this.getInitialFilterValues(),
            query: {},
            rangePeriod: [
                { id: "today", name: "Today" },
                { id: "7_days", name: "Last 7 days" },
                { id: "30_days", name: "Last 30 days" },
                { id: "1_year", name: "Last 1 year" },
                { id: "custom", name: "Custom" },
            ], 
            rangeMap: {
                "today" :"Today",
                "7_days": "Last 7 days",
                "30_days": "Last 30 days",
                "1_year": "Last 1 year",
                "custom": "Custom",
            },
            calendarAttributes: null,
            minmax: {
                minDate: null,
                maxDate: null,
            },
            startDate: null,
            endDate: null,
            combineFilter: false,
        };
    },

    computed: {
        filterByItems() {
            switch (this.page) {
            case "invoices":
                return  [
                    { id: "createdAt", name: "Created At" },
                    { id: "dueDate", name: "Due date"},
                ];

            case "projects":
                return  [
                    { id: "createdAt", name: "Created At" },
                    { id: "deadline", name: "Deadline" },
                ];

            case "tasks":
                return  [
                    { id: "createdAt", name: "Created At" },
                    { id: "deadline", name: "Deadline" },
                ];

            default:
                return  [
                    { id: "createdAt", name: "Created At" },
                ];
            }
        },
    },

    watch: {
        "filter.range": {
            handler(newVal) {
                this.setCalendarAttr(newVal);
            },
        },
    },

    mounted() {
        // this.filter.startDate = this.$route.query.startDate ? new Date(this.$route.query.startDate).toLocaleDateString("en-GB") : null;
        // this.filter.endDate = this.$route.query.endDate ? new Date(this.$route.query.endDate).toLocaleDateString("en-GB") : null;

        this.setCalendarAttr(this.filter.range);
    },

    methods: {
        serilaizeQuery,
        extractDateFilters,
        formatDate,

        setCurrentFilter(val) {            
            this.filter.range = val.id;
        },

        setCalendarAttr(newVal) {            
            if (newVal === "custom") {
                this.minmax = {
                    minDate: null,
                    maxDate: new Date(),
                };
                this.calendarAttributes = null;
                // this.filter.startDate = null;
                // this.filter.endDate = null;

                return;
            } 
            
            const range = this.extractDateFilters(newVal);

            if (range && range.startDate) {
                this.calendarAttributes = [
                    {
                        highlight: "blue",
                        dates: [[new Date(range.startDate), new Date(range.endDate)]],
                    },
                ];
                this.minmax.minDate = range.startDate;
                this.minmax.maxDate = range.endDate;

                this.filter.startDate = range.startDate;
                this.filter.endDate = range.endDate;
            }
        },

        getInitialFilterValues() {            
            return {
                startDate: null,
                endDate: null,
                range: this.$route.query.range || "1_year",
                filterBy: this.$route.query.filterBy || "createdAt", // created at, due date, deadline
            };
        },

        clearFilter() {
            const currentRoute = this.$route.path;
            const queryParam = serilaizeQuery({});
            
            this.filter = this.getInitialFilterValues();
            this.$router.replace(`${currentRoute}?${queryParam}`);
        },

        handleFilter() {
            let query = {
                filterBy: this.filter.filterBy,
                range: this.filter.range,
                ...(this.filter.range === "custom" && {
                    startDate: this.$format_date(this.filter.startDate),
                    endDate: this.$format_date(this.filter.endDate),
                }),
            };

            // if (this.combineFilter) {
            //     query =  {
            //         ...this.$route.query,
            //         ...query,
            //     };   
            // }

            const currentRoute = this.$route.path;
            const queryParam = this.serilaizeQuery(query);

            if (this.pageType === "inner") {
                this.$emit("update:filter", queryParam);
                return; 
            }
                        
            this.$router.replace(`${currentRoute}?${queryParam}`);
        },
    },
};
</script>

<style lang="scss" scoped>
    .dropdown-menu {
        min-width: 12rem !important;
        padding: 0.5rem 1rem;

    }
    
    .date-filter-item {
        padding: 5px 10px 5px 0px;
        display: flex;
        align-items: center;
        color: #4b5563;
        cursor: pointer;

        &.active {
            font-weight: 600 !important;
            color: var(--brand-500) !important;
            background-color: #E3F2F4;
            padding: 5px 10px;
            border-radius: 10px;
        }
    }

    .filter-dates-selection {
        min-width: 100px;
    }

    .filter-dates {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: .5rem
    }
</style>